import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Stanford-inspired palette ─── */
const C = {
  bg: '#1B1B1B',
  surface: '#242424',
  surfaceElevated: '#2D2B2E',
  cardinal: '#8C1515',
  cardinalLight: '#B83A4B',
  cardinalGlow: 'rgba(140,21,21,0.35)',
  paloAlto: '#175E54',
  paloVerde: '#279989',
  sandstone: '#D2C4A5',
  fog: '#DAD7CB',
  white: '#FFFFFF',
  textPrimary: '#F5F5F5',
  textSecondary: '#B8B0A8',
  textMuted: '#7A7267',
  border: 'rgba(255,255,255,0.08)',
};

const fontLink = "https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;600;700;900&family=Source+Serif+4:wght@400;600;700&display=swap";

/* ─── Nav ─── */
const StanfordNav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <nav data-testid="stanford-nav" style={{ fontFamily: "'Source Sans 3', sans-serif" }}
      className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${scrolled ? 'backdrop-blur-xl shadow-lg' : ''}`}>
      <div style={{ background: scrolled ? 'rgba(27,27,27,0.95)' : 'transparent', borderBottom: scrolled ? `1px solid ${C.border}` : 'none', transition: 'all 0.3s' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/versione-a" className="flex items-center gap-3" data-testid="stanford-logo">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: C.cardinal }}>
              <span className="text-white font-black text-sm" style={{ fontFamily: "'Source Serif 4', serif" }}>AI</span>
            </div>
            <div>
              <span className="font-bold text-lg tracking-tight" style={{ color: C.white, fontFamily: "'Source Serif 4', serif" }}>
                &lt;/AI_covo.tech&gt;<sup style={{ fontSize: '8px' }}>®</sup>
              </span>
              <span className="block text-[10px] uppercase tracking-[0.25em] font-semibold" style={{ color: C.sandstone }}>
                Digital Hub Sociale
              </span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {['Doposcuola', 'Formazione', 'Chi Siamo', 'Dona'].map(label => (
              <Link key={label} to={label === 'Chi Siamo' ? '/about' : `/${label.toLowerCase()}`}
                className="text-sm font-semibold uppercase tracking-wider transition-colors hover:opacity-100"
                style={{ color: C.textSecondary, fontFamily: "'Source Sans 3', sans-serif" }}
                onMouseEnter={e => e.target.style.color = C.cardinalLight}
                onMouseLeave={e => e.target.style.color = C.textSecondary}>
                {label}
              </Link>
            ))}
          </div>
          <Link to="/test-ab" className="text-xs px-4 py-2 rounded-full border font-semibold transition-all hover:scale-105"
            style={{ borderColor: C.cardinal, color: C.cardinal }} data-testid="stanford-back-ab">
            A/B Test
          </Link>
        </div>
      </div>
    </nav>
  );
};

/* ─── Hero ─── */
const StanfordHero = () => (
  <section data-testid="stanford-hero" className="relative min-h-screen flex items-center overflow-hidden"
    style={{ background: `linear-gradient(165deg, ${C.bg} 0%, #120808 50%, ${C.bg} 100%)` }}>
    <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: `radial-gradient(circle at 1px 1px, ${C.sandstone} 1px, transparent 0)`,
      backgroundSize: '40px 40px'
    }} />
    <div className="absolute top-0 right-0 w-1/2 h-full opacity-10"
      style={{ background: `radial-gradient(ellipse at 80% 30%, ${C.cardinal}40, transparent 70%)` }} />
    
    <div className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-20 w-full">
      <div className="max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] mb-6"
          style={{ color: C.cardinal, fontFamily: "'Source Sans 3', sans-serif" }}>
          Valdelsa, Toscana — Digital Hub Sociale
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.08] mb-8"
          style={{ color: C.white, fontFamily: "'Source Serif 4', serif" }}>
          Una scuola di visionari,<br />
          <span style={{ color: C.cardinalLight }}>aperta a tutti.</span>
        </h1>
        <p className="text-lg leading-relaxed mb-10 max-w-xl"
          style={{ color: C.textSecondary, fontFamily: "'Source Sans 3', sans-serif" }}>
          Doposcuola, formazione digitale, inclusione sociale e cultura del fare.
          Perché il sapere non ha età, e il futuro si costruisce insieme.
        </p>
        <div className="flex flex-wrap gap-4">
          <Link to="/doposcuola" data-testid="stanford-cta-scopri"
            className="inline-flex items-center gap-2 px-8 py-4 font-bold text-sm uppercase tracking-wider rounded-sm transition-all hover:scale-[1.02]"
            style={{ background: C.cardinal, color: C.white, fontFamily: "'Source Sans 3', sans-serif" }}>
            Scopri il Progetto
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </Link>
          <Link to="/dona" data-testid="stanford-cta-dona"
            className="inline-flex items-center px-8 py-4 font-bold text-sm uppercase tracking-wider rounded-sm border-2 transition-all hover:scale-[1.02]"
            style={{ borderColor: C.sandstone, color: C.sandstone, fontFamily: "'Source Sans 3', sans-serif" }}>
            Sostienici
          </Link>
        </div>
      </div>
    </div>

    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
      <svg className="w-5 h-5" style={{ color: C.textMuted }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    </div>
  </section>
);

/* ─── Pillars ─── */
const StanfordPillars = () => {
  const pillars = [
    { icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', title: 'Doposcuola', desc: 'Supporto quotidiano per bambini e ragazzi 6-18 anni.', accent: C.cardinal },
    { icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', title: 'Formazione Digitale', desc: 'Corsi detraibili al 100% per professionisti.', accent: C.paloVerde },
    { icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', title: 'Inclusione', desc: 'Spazi accessibili per anziani, famiglie ISEE, disabilità.', accent: '#E040FB' },
    { icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z', title: 'Laboratori', desc: 'Arti perdute, artigianato, creatività e maieutica.', accent: C.sandstone },
    { icon: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9', title: 'Cittadino Digitale', desc: 'Alfabetizzazione per chi è rimasto indietro.', accent: '#4285F4' },
    { icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064', title: 'Sport & Benessere', desc: 'Attività sportive e weekend per la comunità.', accent: '#FF6B35' },
  ];

  return (
    <section data-testid="stanford-pillars" className="py-24" style={{ background: C.surface }}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-3" style={{ color: C.cardinal }}>I Nostri Pilastri</p>
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ color: C.white, fontFamily: "'Source Serif 4', serif" }}>
            Un ecosistema per <span style={{ color: C.cardinalLight }}>tutti.</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <div key={i} className="p-6 rounded-lg border transition-all duration-300 hover:-translate-y-1 group"
              style={{ background: C.surfaceElevated, borderColor: C.border }}
              onMouseEnter={e => e.currentTarget.style.borderColor = p.accent + '60'}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                style={{ background: p.accent + '15' }}>
                <svg className="w-6 h-6" style={{ color: p.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={p.icon} />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: C.white, fontFamily: "'Source Sans 3', sans-serif" }}>{p.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: C.textSecondary }}>{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Quote ─── */
const StanfordQuote = () => (
  <section data-testid="stanford-quote" className="py-20 relative overflow-hidden" style={{ background: C.bg }}>
    <div className="absolute inset-0 opacity-5"
      style={{ background: `radial-gradient(ellipse at 50% 50%, ${C.cardinal}, transparent 70%)` }} />
    <div className="relative max-w-4xl mx-auto px-6 text-center">
      <div className="w-16 h-0.5 mx-auto mb-8" style={{ background: C.cardinal }} />
      <blockquote className="text-2xl sm:text-3xl font-bold italic leading-snug mb-6"
        style={{ color: C.sandstone, fontFamily: "'Source Serif 4', serif" }}>
        "Quando entri nell'AI_COVO, qualcuno tirerà fuori il meglio da te."
      </blockquote>
      <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.cardinal }}>Maieutica — Socrate</p>
      <div className="w-16 h-0.5 mx-auto mt-8" style={{ background: C.cardinal }} />
    </div>
  </section>
);

/* ─── Community Counter ─── */
const StanfordCommunity = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/community/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const sections = [
    { key: 'genz', label: 'Agora GenZ', count: stats?.genz || 0, accent: '#FFD700' },
    { key: 'boomer', label: 'Boomer & GenX', count: stats?.boomer || 0, accent: '#CD7F32' },
    { key: 'senatori', label: 'I Senatori', count: stats?.senatori || 0, accent: '#C0C0C0' },
    { key: 'inclusivo', label: 'Spazio Inclusivo', count: stats?.inclusivo || 0, accent: '#E040FB' },
    { key: 'sport', label: 'Sport', count: stats?.sport || 0, accent: '#FF6B35' },
  ];
  const total = sections.reduce((s, x) => s + x.count, 0);

  return (
    <section data-testid="stanford-community" className="py-20" style={{ background: C.surface }}>
      <div className="max-w-5xl mx-auto px-6 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-3" style={{ color: C.cardinal }}>La Comunità Cresce</p>
        <p className="text-5xl sm:text-6xl font-black mb-2" style={{ color: C.white, fontFamily: "'Source Serif 4', serif" }}>{total}</p>
        <p className="text-sm mb-10" style={{ color: C.textSecondary }}>anime al Covo — in tempo reale</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {sections.map(s => (
            <div key={s.key} className="p-4 rounded-lg border text-center" style={{ background: C.surfaceElevated, borderColor: C.border }}>
              <p className="text-2xl font-black" style={{ color: s.accent }}>{s.count}</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold mt-1" style={{ color: C.textMuted }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── CTA Sostienici ─── */
const StanfordCTA = () => (
  <section data-testid="stanford-cta" className="py-24 relative overflow-hidden" style={{ background: C.bg }}>
    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${C.cardinal}08, transparent 50%, ${C.paloAlto}08)` }} />
    <div className="relative max-w-4xl mx-auto px-6 text-center">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: C.paloVerde }}>Sostienici</p>
      <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: C.white, fontFamily: "'Source Serif 4', serif" }}>
        Che cintura sei?
      </h2>
      <p className="text-base mb-10 max-w-lg mx-auto" style={{ color: C.textSecondary }}>
        Come nel karate, ogni livello ha il suo valore. Da 1€ a 999€ aziendale — 
        tutto <strong style={{ color: C.white }}>deducibile al 100%</strong>.
      </p>
      <div className="flex flex-wrap justify-center gap-3 mb-10">
        {[
          { belt: 'Bianca', price: '1€', color: '#fff', border: '#fff' },
          { belt: 'Arancione', price: '5€', color: '#f97316', border: '#f97316' },
          { belt: 'Verde', price: '10€', color: '#22c55e', border: '#22c55e' },
          { belt: 'Blu', price: '15€', color: '#3b82f6', border: '#3b82f6' },
          { belt: 'Rossa', price: '50€', color: '#ef4444', border: '#ef4444' },
        ].map(b => (
          <div key={b.belt} className="px-5 py-3 rounded-lg border text-center min-w-[80px]"
            style={{ borderColor: b.border + '40', background: C.surfaceElevated }}>
            <p className="text-lg font-black" style={{ color: b.color }}>{b.price}</p>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>{b.belt}</p>
          </div>
        ))}
      </div>
      <Link to="/dona" data-testid="stanford-dona-cta"
        className="inline-flex items-center gap-2 px-10 py-4 font-bold text-sm uppercase tracking-wider rounded-sm transition-all hover:scale-[1.02]"
        style={{ background: C.cardinal, color: C.white }}>
        Dona Ora
      </Link>
    </div>
  </section>
);

/* ─── Footer ─── */
const StanfordFooter = () => (
  <footer data-testid="stanford-footer" className="py-16 border-t" style={{ background: C.surface, borderColor: C.border }}>
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid md:grid-cols-4 gap-10 mb-12">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: C.cardinal }}>
              <span className="text-white font-black text-xs">AI</span>
            </div>
            <span className="font-bold" style={{ color: C.white, fontFamily: "'Source Serif 4', serif" }}>AI_covo.tech</span>
          </div>
          <p className="text-sm" style={{ color: C.textMuted }}>Colle di Val d'Elsa (SI)<br />Toscana, Italia</p>
        </div>
        {[
          { title: 'Servizi', links: [['Doposcuola', '/doposcuola'], ['Formazione', '/formazione'], ['Inclusione', '/hub']] },
          { title: 'Risorse', links: [['Brand Kit', '/brand'], ['Sostienici', '/dona'], ['RoadMap', '/links']] },
          { title: 'Contatti', links: [['info@aicovo.it', '#'], ['WhatsApp', 'https://wa.me/393273839782']] },
        ].map(col => (
          <div key={col.title}>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: C.cardinal }}>{col.title}</h4>
            <ul className="space-y-2">
              {col.links.map(([label, href]) => (
                <li key={label}>
                  <Link to={href} className="text-sm transition-colors" style={{ color: C.textMuted }}
                    onMouseEnter={e => e.target.style.color = C.white}
                    onMouseLeave={e => e.target.style.color = C.textMuted}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4" style={{ borderColor: C.border }}>
        <p className="text-xs" style={{ color: C.textMuted }}>© 2030 AI_COVO · Digital Hub Sociale</p>
        <p className="text-xs" style={{ color: C.textMuted }}>&lt;/BUILT_FOR_COMMUNITY/&gt;</p>
      </div>
    </div>
  </footer>
);

/* ─── Main Page ─── */
const VersioneStanfordPage = () => (
  <div data-testid="versione-stanford-page" style={{ background: C.bg, color: C.textPrimary, fontFamily: "'Source Sans 3', sans-serif" }}>
    <link href={fontLink} rel="stylesheet" />
    <StanfordNav />
    <StanfordHero />
    <StanfordPillars />
    <StanfordQuote />
    <StanfordCommunity />
    <StanfordCTA />
    <StanfordFooter />
  </div>
);

export default VersioneStanfordPage;
